import { useState, useEffect } from 'react';
import { apiFetch } from '../App';
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const BOOKMAKER_COLORS = {
  betway_bw: '#00e5b0', pstbets_bw: '#3d8ef8', sunbet_bw: '#f5a623',
  bet267_bw: '#f04747', bbets_bw: '#12c98e', betxplosion_bw: '#a855f7',
};


function formatKickoff(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-BW', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function OddsSparkline({ snapshots, color }) {
  if (!snapshots?.length) return (
    <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '12px 0' }}>
      No movement data yet — accumulating...
    </div>
  );
  const filtered = [...snapshots]
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));
  if (filtered.length < 2) return (
    <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '12px 0' }}>
      Need more data points...
    </div>
  );
  const prices = filtered.map(s => s.price);
  const min = Math.min(...prices) * 0.98;
  const max = Math.max(...prices) * 1.02;
  const W = 300, H = 60;
  const points = filtered.map((s, i) => {
    const x = (i / (filtered.length - 1)) * W;
    const y = H - ((s.price - min) / (max - min)) * H;
    return `${x},${y}`;
  }).join(' ');
  const lastPrice = prices[prices.length - 1];
  const firstPrice = prices[0];
  const changed = lastPrice !== firstPrice;
  const up = lastPrice > firstPrice;
  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
        <polyline points={points} fill="none" stroke={color || '#00e5b0'} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {filtered.map((s, i) => {
          const x = (i / (filtered.length - 1)) * W;
          const y = H - ((s.price - min) / (max - min)) * H;
          return <circle key={i} cx={x} cy={y} r="3" fill={color || '#00e5b0'} />;
        })}
      </svg>
      {changed && (
        <div style={{ fontSize: 11, marginTop: 4, color: up ? 'var(--success)' : 'var(--danger)' }}>
          {up ? '↑' : '↓'} {firstPrice} → {lastPrice}
        </div>
      )}
    </div>
  );
}

export default function MatchDetail({ item, onBack }) {
  const [bookmakerOdds, setBookmakerOdds] = useState([]);
  const [bestPrices, setBestPrices] = useState({});
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailMarket, setDetailMarket] = useState('1x2');
  const [width, setWidth] = useState(window.innerWidth);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [movementMarket, setMovementMarket] = useState('1x2');

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  useEffect(() => { loadData(); }, [item]);

  async function loadData() {
    setLoading(true);
    try {
      const [data, snapData] = await Promise.all([
        apiFetch(`/best-odds/match/${encodeURIComponent(item.normalizedMatchKey)}`),
        apiFetch(`/best-odds/snapshots/${encodeURIComponent(item.normalizedMatchKey)}`).catch(() => ({ snapshots: [] })),
      ]);
      setBookmakerOdds(data.bookmakers || []);
      setBestPrices({
        home: data.bestHome?.price,
        draw: data.bestDraw?.price,
        away: data.bestAway?.price,
      });
      setSnapshots(snapData.snapshots || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function loadAIAnalysis() {
  setAiLoading(true);
  setAiError(null);
  try {
    const data = await apiFetch('/match-analyst', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ normalizedMatchKey: item.normalizedMatchKey }),
    });
    setAiAnalysis(data);
  } catch (err) {
    setAiError('Could not generate analysis. Please try again.');
  } finally {
    setAiLoading(false);
  }
}

  const bestHomeBook = bookmakerOdds.find(b => b.home?.price === bestPrices.home);
  const bestDrawBook = bookmakerOdds.find(b => b.draw?.price === bestPrices.draw);
  const bestAwayBook = bookmakerOdds.find(b => b.away?.price === bestPrices.away);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }} onClick={onBack}>
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 15 : 22, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.homeTeam} vs {item.awayTeam}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            {item.league} · {formatKickoff(item.commenceTime)}
          </div>
        </div>
        <span className={`pill ${item.status === 'ARB' ? 'pill-green' : item.status === 'NEAR_ARB' ? 'pill-amber' : 'pill-blue'}`} style={{ flexShrink: 0 }}>
          {item.status === 'ARB' ? '⚡ ARB' : item.status === 'NEAR_ARB' ? 'Near Arb' : 'No Arb'}
        </span>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: 10 }}>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent)' }}>
          <div className="stat-label">Book %</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 14 : 22, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.bookPercent?.toFixed(2)}%</div>
          <div className="stat-change neutral">Combined implied prob</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': item.arbMargin > 0 ? 'var(--success)' : 'var(--danger)' }}>
          <div className="stat-label">Margin</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 14 : 22, color: item.arbMargin > 0 ? 'var(--success)' : 'var(--danger)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.arbMargin > 0 ? '+' : ''}{item.arbMargin?.toFixed(2)}%
          </div>
          <div className={`stat-change ${item.arbMargin > 0 ? 'up' : 'down'}`}>
            {item.arbMargin > 0 ? 'Profit available' : 'No arb currently'}
          </div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent2)' }}>
          <div className="stat-label">Bookmakers</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 14 : 22 }}>{bookmakerOdds.length}</div>
          <div className="stat-change neutral">→ All BW bookmakers</div>
        </div>
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>

        {/* Odds comparison table */}
<div className="card" style={{ overflow: 'hidden' }}>
  <div className="section-header">
    <div>
      <div className="section-title">📊 Odds Comparison</div>
      <div className="section-sub">Best price highlighted ✓</div>
    </div>
  </div>

  {/* Market selector */}
  <div style={{ padding: '8px 16px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {[
      { key: '1x2', label: '1X2' },
      { key: 'ou25', label: 'O/U 2.5' },
      { key: 'ou15', label: 'O/U 1.5' },
      { key: 'ou35', label: 'O/U 3.5' },
      { key: 'btts', label: 'BTTS' },
      { key: 'dc', label: 'Double Chance' },
    ].map(m => (
      <button
        key={m.key}
        onClick={() => setDetailMarket(m.key)}
        className={detailMarket === m.key ? 'btn btn-primary' : 'btn btn-outline'}
        style={{ fontSize: 11, padding: '4px 10px' }}
      >
        {m.label}
      </button>
    ))}
  </div>

  <div style={{ overflowX: 'auto' }}>
    {detailMarket === '1x2' ? (
      <table className="data-table" style={{ minWidth: isMobile ? 340 : 'auto' }}>
        <thead>
          <tr>
            <th>Bookmaker</th>
            <th style={{ textAlign: 'center' }}>Home</th>
            <th style={{ textAlign: 'center' }}>Draw</th>
            <th style={{ textAlign: 'center' }}>Away</th>
            <th style={{ textAlign: 'right' }}>Margin</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5}><div className="skeleton" style={{ height: 40, margin: 8 }} /></td></tr>
          ) : bookmakerOdds.length === 0 ? (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No odds data available</td></tr>
          ) : bookmakerOdds.map(bm => (
            <tr key={bm.bookmaker}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[bm.bookmaker] || 'var(--accent)', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{BOOKMAKER_LABELS[bm.bookmaker] || bm.bookmaker}</span>
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <PriceCell price={bm.home?.price} isBest={bm.home?.price === bestPrices.home} />
              </td>
              <td style={{ textAlign: 'center' }}>
                <PriceCell price={bm.draw?.price} isBest={bm.draw?.price === bestPrices.draw} />
              </td>
              <td style={{ textAlign: 'center' }}>
                <PriceCell price={bm.away?.price} isBest={bm.away?.price === bestPrices.away} />
              </td>
              <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                {bm.margin != null ? `${bm.margin > 0 ? '+' : ''}${bm.margin}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      /* O/U and BTTS — show best odds from BestOdds item */
      <table className="data-table" style={{ minWidth: isMobile ? 280 : 'auto' }}>
        <thead>
          <tr>
            <th>Market</th>
            <th style={{ textAlign: 'center' }}>Best Price</th>
            <th style={{ textAlign: 'center' }}>Bookmaker</th>
          </tr>
        </thead>
        <tbody>
          {detailMarket === 'ou15' && (
            <>
              <tr>
                <td style={{ fontWeight: 500 }}>Over 1.5</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestOver15?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestOver15?.bookmaker] || '—'}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Under 1.5</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestUnder15?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestUnder15?.bookmaker] || '—'}</td>
              </tr>
            </>
          )}
          {detailMarket === 'ou25' && (
            <>
              <tr>
                <td style={{ fontWeight: 500 }}>Over 2.5</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestOver25?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestOver25?.bookmaker] || '—'}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Under 2.5</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestUnder25?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestUnder25?.bookmaker] || '—'}</td>
              </tr>
            </>
          )}
          {detailMarket === 'ou35' && (
            <>
              <tr>
                <td style={{ fontWeight: 500 }}>Over 3.5</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestOver35?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestOver35?.bookmaker] || '—'}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Under 3.5</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestUnder35?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestUnder35?.bookmaker] || '—'}</td>
              </tr>
            </>
          )}
          {detailMarket === 'btts' && (
            <>
              <tr>
                <td style={{ fontWeight: 500 }}>BTTS Yes</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestBttsYes?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestBttsYes?.bookmaker] || '—'}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>BTTS No</td>
                <td style={{ textAlign: 'center' }}><PriceCell price={item.bestBttsNo?.price} isBest={true} /></td>
                <td style={{ textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>{BOOKMAKER_LABELS[item.bestBttsNo?.bookmaker] || '—'}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    )}

  {/* Double Chance table */}
      {detailMarket === 'dc' && (
        <table className="data-table" style={{ minWidth: isMobile ? 340 : 'auto' }}>
          <thead>
            <tr>
              <th>Bookmaker</th>
              <th style={{ textAlign: 'center' }}>1X (Home/Draw)</th>
              <th style={{ textAlign: 'center' }}>X2 (Draw/Away)</th>
              <th style={{ textAlign: 'center' }}>12 (Home/Away)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4}><div className="skeleton" style={{ height: 40, margin: 8 }} /></td></tr>
            ) : bookmakerOdds.length === 0 ? (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No odds data available</td></tr>
            ) : (() => {
              const best1X = Math.max(...bookmakerOdds.filter(bm => bm.home?.price && bm.draw?.price).map(bm => Number((1 / (1/bm.home.price + 1/bm.draw.price)).toFixed(2))));
              const bestX2 = Math.max(...bookmakerOdds.filter(bm => bm.draw?.price && bm.away?.price).map(bm => Number((1 / (1/bm.draw.price + 1/bm.away.price)).toFixed(2))));
              const best12 = Math.max(...bookmakerOdds.filter(bm => bm.home?.price && bm.away?.price).map(bm => Number((1 / (1/bm.home.price + 1/bm.away.price)).toFixed(2))));
              return bookmakerOdds.map(bm => {
                if (!bm.home?.price || !bm.draw?.price || !bm.away?.price) return null;
                const dc1X = Number((1 / (1/bm.home.price + 1/bm.draw.price)).toFixed(2));
                const dcX2 = Number((1 / (1/bm.draw.price + 1/bm.away.price)).toFixed(2));
                const dc12 = Number((1 / (1/bm.home.price + 1/bm.away.price)).toFixed(2));
                return (
                  <tr key={bm.bookmaker}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[bm.bookmaker] || 'var(--accent)', flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 500 }}>{BOOKMAKER_LABELS[bm.bookmaker] || bm.bookmaker}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <PriceCell price={dc1X} isBest={dc1X === best1X} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <PriceCell price={dcX2} isBest={dcX2 === bestX2} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <PriceCell price={dc12} isBest={dc12 === best12} />
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      )}
  </div>

  {/* Best odds summary — Double Chance */}
  {detailMarket === 'dc' && !loading && bookmakerOdds.length > 0 && (() => {
    const bms = bookmakerOdds.filter(bm => bm.home?.price && bm.draw?.price && bm.away?.price);
    const best1X = bms.reduce((best, bm) => {
      const v = Number((1 / (1/bm.home.price + 1/bm.draw.price)).toFixed(2));
      return v > best.price ? { price: v, bookmaker: bm.bookmaker } : best;
    }, { price: 0, bookmaker: null });
    const bestX2 = bms.reduce((best, bm) => {
      const v = Number((1 / (1/bm.draw.price + 1/bm.away.price)).toFixed(2));
      return v > best.price ? { price: v, bookmaker: bm.bookmaker } : best;
    }, { price: 0, bookmaker: null });
    const best12 = bms.reduce((best, bm) => {
      const v = Number((1 / (1/bm.home.price + 1/bm.away.price)).toFixed(2));
      return v > best.price ? { price: v, bookmaker: bm.bookmaker } : best;
    }, { price: 0, bookmaker: null });
    return (
      <div style={{ padding: '14px 16px', background: 'rgba(0,229,176,0.04)', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <BestCell label={`1X — ${BOOKMAKER_LABELS[best1X.bookmaker] || '—'}`} price={best1X.price} />
        <BestCell label={`X2 — ${BOOKMAKER_LABELS[bestX2.bookmaker] || '—'}`} price={bestX2.price} />
        <BestCell label={`12 — ${BOOKMAKER_LABELS[best12.bookmaker] || '—'}`} price={best12.price} />
      </div>
    );
  })()}

  {/* Best odds summary — only for 1X2 */}
  {detailMarket === '1x2' && !loading && bookmakerOdds.length > 0 && (
    <div style={{ padding: '14px 16px', background: 'rgba(0,229,176,0.04)', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
      <BestCell label={`Home — ${BOOKMAKER_LABELS[bestHomeBook?.bookmaker] || '—'}`} price={bestPrices.home} />
      <BestCell label={`Draw — ${BOOKMAKER_LABELS[bestDrawBook?.bookmaker] || '—'}`} price={bestPrices.draw} />
      <BestCell label={`Away — ${BOOKMAKER_LABELS[bestAwayBook?.bookmaker] || '—'}`} price={bestPrices.away} />
    </div>
  )}
</div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ArbCalculator bestPrices={bestPrices} bestHomeBook={bestHomeBook} bestDrawBook={bestDrawBook} bestAwayBook={bestAwayBook} />
          <HowToBet bestPrices={bestPrices} bestHomeBook={bestHomeBook} bestDrawBook={bestDrawBook} bestAwayBook={bestAwayBook} />
        </div>
      </div>

{/* Odds movement */}
<div className="card">
  <div className="section-header">
    <div>
      <div className="section-title">📈 Odds Movement</div>
      <div className="section-sub">Price changes over time</div>
    </div>
  </div>

  {/* Market selector */}
  <div style={{ padding: '0 16px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {[
      { key: '1x2', label: '1X2' },
      { key: 'ou25', label: 'O/U 2.5' },
      { key: 'ou15', label: 'O/U 1.5' },
      { key: 'ou35', label: 'O/U 3.5' },
      { key: 'btts', label: 'BTTS' },
    ].map(m => (
      <button
        key={m.key}
        onClick={() => setMovementMarket(m.key)}
        className={movementMarket === m.key ? 'btn btn-primary' : 'btn btn-outline'}
        style={{ fontSize: 11, padding: '4px 10px' }}
      >
        {m.label}
      </button>
    ))}
  </div>

  <div style={{ padding: '0 16px 16px' }}>
    {movementMarket === '1x2' && (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 16 }}>
        {[
          { label: `Home — ${item.homeTeam}`, bookmaker: bestHomeBook?.bookmaker, outcomeNames: ['home', '1', item.homeTeam?.toLowerCase()] },
          { label: 'Draw', bookmaker: bestDrawBook?.bookmaker, outcomeNames: ['draw', 'x'] },
          { label: `Away — ${item.awayTeam}`, bookmaker: bestAwayBook?.bookmaker, outcomeNames: ['away', '2', item.awayTeam?.toLowerCase()] },
        ].map(leg => {
          let filtered = snapshots.filter(s =>
            s.bookmaker === leg.bookmaker &&
            leg.outcomeNames.includes(s.outcomeName?.toLowerCase().trim())
          );
          if (!filtered.length) {
            filtered = snapshots.filter(s =>
              leg.outcomeNames.includes(s.outcomeName?.toLowerCase().trim())
            );
          }
          return (
            <div key={leg.label}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>
                {leg.label}
                <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>via {BOOKMAKER_LABELS[leg.bookmaker] || '—'}</span>
              </div>
              <OddsSparkline snapshots={filtered} color={BOOKMAKER_COLORS[leg.bookmaker]} />
            </div>
          );
        })}
      </div>
    )}

    {['ou15', 'ou25', 'ou35'].includes(movementMarket) && (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 16 }}>
        {[
          { label: `Over ${movementMarket === 'ou15' ? '1.5' : movementMarket === 'ou25' ? '2.5' : '3.5'}`, outcomeName: 'over', handicap: movementMarket === 'ou15' ? 1.5 : movementMarket === 'ou25' ? 2.5 : 3.5 },
          { label: `Under ${movementMarket === 'ou15' ? '1.5' : movementMarket === 'ou25' ? '2.5' : '3.5'}`, outcomeName: 'under', handicap: movementMarket === 'ou15' ? 1.5 : movementMarket === 'ou25' ? 2.5 : 3.5 },
        ].map(leg => {
          const filtered = snapshots.filter(s =>
            s.outcomeName?.toLowerCase() === leg.outcomeName &&
            s.handicap === leg.handicap
          );
          const bookmaker = filtered[0]?.bookmaker;
          return (
            <div key={leg.label}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>
                {leg.label}
                {bookmaker && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>via {BOOKMAKER_LABELS[bookmaker] || '—'}</span>}
              </div>
              <OddsSparkline snapshots={filtered} color="var(--accent)" />
            </div>
          );
        })}
      </div>
    )}

    {movementMarket === 'btts' && (
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 16 }}>
        {[
          { label: 'BTTS Yes', outcomeName: 'yes' },
          { label: 'BTTS No', outcomeName: 'no' },
        ].map(leg => {
          const filtered = snapshots.filter(s =>
            s.outcomeName?.toLowerCase() === leg.outcomeName
          );
          const bookmaker = filtered[0]?.bookmaker;
          return (
            <div key={leg.label}>
              <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>
                {leg.label}
                {bookmaker && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>via {BOOKMAKER_LABELS[bookmaker] || '—'}</span>}
              </div>
              <OddsSparkline snapshots={filtered} color="var(--accent2)" />
            </div>
          );
        })}
      </div>
    )}

    <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)' }}>
      💡 Odds movement data accumulates over time as prices change. The more scrape cycles that run, the richer this chart becomes.
    </div>
  </div>


{/* AI Match Analyst */}
<div className="card" style={{ marginTop: 16 }}>
  <div className="section-header">
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
  <div className="section-title">🤖 AI Match Analyst</div>
  <span className="pill pill-blue" style={{ fontSize: 9 }}>BETA</span>
</div>
<div className="section-sub">Powered by Groq AI — analyses odds, sharp money and value</div>
    </div>
  </div>
  <div style={{ padding: '0 16px 16px' }}>
    {!aiAnalysis && !aiLoading && (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
          Get an AI-powered breakdown of this match including value opportunities, sharp money signals and betting recommendations.
        </div>
        <button
          className="btn btn-primary"
          onClick={loadAIAnalysis}
          style={{ fontSize: 13, padding: '10px 24px' }}
        >
          🤖 Analyse This Match
        </button>
      </div>
    )}

    {aiLoading && (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div className="skeleton" style={{ height: 20, marginBottom: 8 }} />
        <div className="skeleton" style={{ height: 20, marginBottom: 8 }} />
        <div className="skeleton" style={{ height: 20, marginBottom: 8 }} />
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>
          AI is analysing odds and sharp money signals...
        </div>
      </div>
    )}

    {aiError && (
      <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--danger)', fontSize: 13 }}>
        {aiError}
        <button className="btn btn-outline" style={{ marginLeft: 12, fontSize: 11 }} onClick={loadAIAnalysis}>
          Retry
        </button>
      </div>
    )}

    {aiAnalysis && !aiLoading && (
      <div>
        <div style={{
  background: 'var(--bg3)', borderRadius: 'var(--radius-sm)',
  padding: '14px 16px', fontSize: 13, lineHeight: 1.8,
  color: 'var(--text2)',
}}>
  {aiAnalysis.analysis.split('\n').map((line, i) => {
    // Convert **text** to bold
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <div key={i} style={{ marginBottom: line.trim() === '' ? 8 : 4 }}>
        {parts.map((part, j) =>
          j % 2 === 1
            ? <strong key={j} style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{part}</strong>
            : <span key={j}>{part}</span>
        )}
      </div>
    );
  })}
</div>
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            Generated {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleTimeString() : ''}
            {aiAnalysis.fromCache ? ' · cached' : ''}
          </span>
          <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => { setAiAnalysis(null); }}>
            ↺ Regenerate
          </button>
        </div>
        <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 11, color: 'var(--text3)' }}>
          ⚠️ AI analysis is for informational purposes only. Always verify odds directly with bookmakers before betting.
        </div>
      </div>
    )}
  </div>
</div>
      </div>
    </div>
  );
}

function PriceCell({ price, isBest }) {
  if (!price) return <span style={{ color: 'var(--text3)' }}>—</span>;
  return (
    <span style={{
      fontFamily: 'var(--font-display)', fontWeight: isBest ? 700 : 400,
      fontSize: isBest ? 14 : 13,
      color: isBest ? 'var(--accent)' : 'var(--text2)',
      background: isBest ? 'rgba(0,229,176,0.1)' : 'transparent',
      padding: isBest ? '2px 6px' : '2px 0', borderRadius: 4,
      display: 'inline-block',
    }}>
      {price} {isBest && '✓'}
    </span>
  );
}

function BestCell({ label, price }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18, color: 'var(--accent)' }}>{price ?? '—'}</div>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{label}</div>
    </div>
  );
}

function ArbCalculator({ bestPrices, bestHomeBook, bestDrawBook, bestAwayBook }) {
  const [stake, setStake] = useState(100);
  const h = bestPrices.home || 0;
  const d = bestPrices.draw || 0;
  const a = bestPrices.away || 0;
  const total = h && d && a ? (1/h) + (1/d) + (1/a) : 1;
  const isArb = total < 1;
  const homeStake = isArb ? ((stake * (1/h)) / total).toFixed(2) : (stake / 3).toFixed(2);
  const drawStake = isArb ? ((stake * (1/d)) / total).toFixed(2) : (stake / 3).toFixed(2);
  const awayStake = isArb ? ((stake * (1/a)) / total).toFixed(2) : (stake / 3).toFixed(2);
  const returns = isArb ? (homeStake * h).toFixed(2) : '—';
  const profit = isArb ? (returns - stake).toFixed(2) : '—';

  return (
    <div className="card">
      {/* Fix: ARB icon centered in bubble */}
      <div className="section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, background: 'rgba(0,229,176,0.1)', borderRadius: '50%' }}>⚡</span>
          <div className="section-title">Arb Calculator</div>
        </div>
      </div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Total stake (BWP)</label>
          <input type="number" className="input-field" value={stake} onChange={e => setStake(parseFloat(e.target.value) || 100)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            { label: `Home — ${BOOKMAKER_LABELS[bestHomeBook?.bookmaker] || '—'}`, odds: h, stakeVal: homeStake },
            { label: `Draw — ${BOOKMAKER_LABELS[bestDrawBook?.bookmaker] || '—'}`, odds: d, stakeVal: drawStake },
            { label: `Away — ${BOOKMAKER_LABELS[bestAwayBook?.bookmaker] || '—'}`, odds: a, stakeVal: awayStake },
          ].map(leg => (
            <div key={leg.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{leg.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>@ {leg.odds}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', fontSize: 16 }}>P{leg.stakeVal}</div>
            </div>
          ))}
        </div>
        {isArb ? (
          <div style={{ background: 'rgba(18,201,142,0.08)', border: '1px solid rgba(18,201,142,0.2)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Guaranteed return</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>P{returns}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Guaranteed profit</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--success)' }}>+P{profit}</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>
            No arb — book% is {(total * 100).toFixed(2)}%
          </div>
        )}
      </div>
    </div>
  );
}

function HowToBet({ bestPrices, bestHomeBook, bestDrawBook, bestAwayBook }) {
  return (
    <div className="card">
      <div className="section-header"><div className="section-title">🎯 Where to bet</div></div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { outcome: 'Home', book: bestHomeBook, price: bestPrices.home },
          { outcome: 'Draw', book: bestDrawBook, price: bestPrices.draw },
          { outcome: 'Away', book: bestAwayBook, price: bestPrices.away },
        ].map(leg => (
          <div key={leg.outcome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{leg.outcome}</div>
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2 }}>{BOOKMAKER_LABELS[leg.book?.bookmaker] || '—'}</div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>{leg.price ?? '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}